import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, IconButton } from '@mui/material';
import { styled } from '@mui/system';
import { FaSignOutAlt } from 'react-icons/fa';
import AppColors from '../../utils/AppColors';
import { useAuth } from '../../context/AuthContext';

// Use the same pattern image path as vendor
const patternImage = '/images/Pattern.png';

const Sidebar = ({ isOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // Define admin navigation items
    const navItems = [
        { path: '/dashboard', label: 'Dashboard', icon: '/icons/explore.png' },
        { path: '/users', label: 'Users', icon: '/icons/profile.png' },
        { path: '/vendors', label: 'Vendors', icon: '/icons/bids.png' },
        { path: '/voucher-suppliers', label: 'Voucher Suppliers', icon: '/icons/cards_display.png' },
        { path: '/vouchers', label: 'All Vouchers', icon: '/icons/purchases.png' },
        { path: '/transactions', label: 'Transactions', icon: '/icons/wallet.png' },
        { path: '/analytics', label: 'Analytics', icon: '/icons/filter.png' },
        { path: '/system', label: 'System', icon: '/icons/settings.png' },
        { path: '/settings', label: 'Settings', icon: '/icons/contact.png' },
    ];

    // Function to check if a route is active
    const isActiveRoute = (path) => {
        const currentPath = location.pathname;
        
        if (path === '/dashboard') {
            return currentPath === '/dashboard';
        }
        
        return currentPath === path || 
               (path !== '/dashboard' && currentPath.startsWith(path));
    };

    // Function to get styles for nav buttons
    const getNavButtonStyle = (path) => {
        const active = isActiveRoute(path);
        return {
            ...styles.navButton,
            ...(active ? styles.activeNavButton : {}),
        };
    };

    return (
        <div style={{ ...styles.sidebar, width: isOpen ? '220px' : '70px' }}>
            {isOpen ? (
                <>  
                    <br/><br/>
                    <div style={styles.logoContainer}>
                        <div style={styles.logoBackground}></div>
                        <img src="/jaddid-logo.png" alt="Jaddid Admin" style={styles.logo} />
                    </div>
                    <nav style={styles.nav}>
                        {navItems.map((item) => {
                            const active = isActiveRoute(item.path);
                            return (
                                <button 
                                    key={item.path}
                                    onClick={() => navigate(item.path)} 
                                    style={getNavButtonStyle(item.path)}
                                >
                                    <img 
                                        src={item.icon} 
                                        alt={`${item.label} icon`}
                                        style={{
                                            ...styles.icon,
                                            filter: active ? 'brightness(0) invert(1)' : 'none'
                                        }}
                                    />
                                    {item.label}
                                </button>
                            );
                        })}
                    </nav>
                    <div style={styles.userInfoContainer}>
                        <div style={styles.userInfo}>
                            <div style={styles.userInfoRow}>
                                <div style={styles.userDetails}>
                                    <Typography variant="subtitle1" style={styles.userName}>
                                        {user?.name || 'Admin'}
                                    </Typography>
                                    <Typography variant="caption" style={styles.userEmail}>
                                        {user?.email || 'admin@jaddid.com'}
                                    </Typography>
                                </div>
                                <IconButton 
                                    onClick={handleLogout} 
                                    style={styles.logoutIcon}
                                    size="small"
                                >
                                    <FaSignOutAlt />
                                </IconButton>
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <br/><br/>
                    <div style={styles.collapsedLogoContainer}>
                        <div style={styles.collapsedLogoBackground}></div>
                        <div style={styles.collapsedLogoRow}>
                            <img src="/jaddid-logo.png" alt="Jaddid Admin" style={styles.collapsedLogo} />
                        </div>
                    </div>
                    <nav style={styles.collapsedNav}>
                        {navItems.map((item) => {
                            const isActive = isActiveRoute(item.path);
                            return (
                                <button 
                                    key={item.path}
                                    onClick={() => navigate(item.path)} 
                                    style={{
                                        ...styles.collapsedNavButton,
                                        ...(isActive && {
                                            backgroundColor: AppColors.primaryDark,
                                        }),
                                    }}
                                    title={item.label}
                                >
                                    <img 
                                        src={item.icon} 
                                        alt={`${item.label} icon`}
                                        style={{
                                            width: '24px',
                                            height: '24px',
                                            filter: isActive ? 'brightness(0) invert(1)' : 'none'
                                        }}
                                    />
                                </button>
                            );
                        })}
                    </nav>
                    <div style={styles.collapsedUserInfo}>
                        <IconButton 
                            onClick={handleLogout} 
                            style={styles.collapsedLogoutButton}
                            size="small"
                            title="Logout"
                        >
                            <FaSignOutAlt />
                        </IconButton>
                    </div>
                </>
            )}
        </div>
    );
};

// Styles (copied from vendor sidebar)
const styles = {
    sidebar: {
        height: '100vh',
        backgroundColor: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '1rem',
        position: 'fixed',
        top: 0,
        left: 0,
        overflowX: 'hidden',
        transition: 'width 0.3s ease',
        boxShadow: 'none',
        zIndex: 1000,
    },
    logoContainer: {
        marginTop: '1rem',
        marginBottom: '1rem',
        textAlign: 'center',
        position: 'relative',
    },
    logoBackground: {
        position: 'absolute',
        top: -40,
        left: -70,
        width: '300px',
        height: '300px',
        backgroundImage: `url(${patternImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: 0.2,
        zIndex: -1,
        pointerEvents: 'none',
    },
    logo: {
        width: '120px',
        position: 'relative',
        zIndex: 1,
    },
    nav: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        flex: 1,
        alignItems: 'flex-start',
    },
    navButton: {
        color: AppColors.primaryDark,
        margin: '0.3rem 0',
        width: '100%',
        textAlign: 'left',
        padding: '0.8rem',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        transition: 'all 0.2s ease',
        border: 'none',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
        },
    },
    icon: {
        marginRight: '10px',
        width: '20px',
        height: '20px',
        display: 'flex',
        justifyContent: 'center',
    },
    userInfoContainer: {
        width: 'auto',
        marginTop: 'auto',
        paddingTop: '1rem',
        marginBottom: '2.5rem',
        marginLeft: '0.5rem',
        marginRight: '2rem',
    },
    userInfo: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        padding: '0.8rem',
        borderRadius: '25px',
        width: '95%',
    },
    userInfoRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
    },
    userDetails: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    userName: {
        fontWeight: 'bold',
        color: AppColors.primaryDark,
        fontSize: '0.9rem',
        lineHeight: 1.2,
    },
    userEmail: {
        color: AppColors.textSecondary,
        fontSize: '0.75rem',
    },
    logoutIcon: {
        color: AppColors.primaryDark,
        padding: '4px',
        '&:hover': {
            color: AppColors.errorRed,
        },
    },
    collapsedLogoContainer: {
        marginTop: '2rem',
        textAlign: 'center',
        position: 'relative',
    },
    collapsedLogoBackground: {
        position: 'absolute',
        top: -40,
        left: -20,
        width: '150px',
        height: '150px',
        backgroundImage: `url(${patternImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: 0.2,
        zIndex: -1,
        pointerEvents: 'none',
    },
    collapsedLogoRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    collapsedLogo: {
        width: '70px',
        marginRight: '5px',
    },
    collapsedNav: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        flex: 1,
        alignItems: 'center',
    },
    collapsedNavButton: {
        color: AppColors.primaryDark,
        margin: '0.8rem 0',
        width: '100%',
        padding: '0.8rem 0',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        border: 'none',
        cursor: 'pointer',
        backgroundColor: 'transparent',
    },
    collapsedUserInfo: {
        marginTop: 'auto',
        marginBottom: '1.5rem',
        display: 'flex',
        justifyContent: 'center',
    },
    collapsedLogoutButton: {
        color: AppColors.primaryDark,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        padding: '8px',
        borderRadius: '50%',
        '&:hover': {
            color: AppColors.errorRed,
        },
        marginBottom: '1rem',
    },
    activeNavButton: {
        backgroundColor: AppColors.primaryDark,
        color: AppColors.textWhite,
        '&:hover': {
            backgroundColor: AppColors.primaryDark,
        },
    },
};

export default Sidebar;
